import axiosInstance from "../../axios/axiosInstance";
import {
  Campus,
  Node,
  Camera,
  CreateCampusDto,
  UpdateCampusDto,
  CreateNodeDto,
  UpdateNodeDto,
  GenerateTokenDto,
  CreateCameraDto,
  UpdateCameraDto,
} from "./types";

export interface ApiResponse<T> {
  data: T;
  status: "ok" | "error";
  error?: { message: string; code: string };
}

export class CampusApi {
  private baseUrl = "/api/vehicles";

  // Campuses
  async getAllCampuses(): Promise<Campus[]> {
    const response = await axiosInstance.get<ApiResponse<Campus[]>>(`${this.baseUrl}/campuses`);
    return response.data?.data || [];
  }

  async getCampusById(id: number): Promise<Campus> {
    const response = await axiosInstance.get<ApiResponse<Campus>>(`${this.baseUrl}/campuses/${id}`);
    return response.data?.data || response.data;
  }

  async createCampus(data: CreateCampusDto): Promise<Campus> {
    const response = await axiosInstance.post<ApiResponse<Campus>>(`${this.baseUrl}/campuses`, data);
    return response.data?.data || response.data;
  }

  async updateCampus(id: number, data: UpdateCampusDto): Promise<Campus> {
    const response = await axiosInstance.put<ApiResponse<Campus>>(`${this.baseUrl}/campuses/${id}`, data);
    return response.data?.data || response.data;
  }

  async deleteCampus(id: number): Promise<void> {
    await axiosInstance.delete(`${this.baseUrl}/campuses/${id}`);
  }

  // Nodes
  async getNodesByCampus(campusId: number): Promise<Node[]> {
    const response = await axiosInstance.get<ApiResponse<Node[]>>(`${this.baseUrl}/campuses/${campusId}/nodes`);
    return response.data?.data || [];
  }

  async getAllNodes(): Promise<Node[]> {
    const response = await axiosInstance.get<ApiResponse<Node[]>>(`${this.baseUrl}/nodes`);
    return response.data?.data || [];
  }

  async getNodeById(nodeId: string): Promise<Node> {
    const response = await axiosInstance.get<ApiResponse<Node>>(`${this.baseUrl}/nodes/${nodeId}`);
    return response.data?.data || response.data;
  }

  async createNode(data: CreateNodeDto): Promise<Node> {
    const response = await axiosInstance.post<ApiResponse<Node>>(`${this.baseUrl}/nodes`, data);
    return response.data?.data || response.data;
  }

  async updateNode(nodeId: number, data: UpdateNodeDto): Promise<Node> {
    const response = await axiosInstance.put<ApiResponse<Node>>(`${this.baseUrl}/nodes/${nodeId}`, data);
    return response.data?.data || response.data;
  }

  async deleteNode(nodeId: string): Promise<void> {
    await axiosInstance.delete(`${this.baseUrl}/nodes/${nodeId}`);
  }

  async generateNodeToken(data: GenerateTokenDto): Promise<Node> {
    const response = await axiosInstance.post<ApiResponse<Node>>(`${this.baseUrl}/nodes/generate-token`, data);
    return response.data?.data || response.data;
  }

  async regenerateNodeToken(data: GenerateTokenDto): Promise<Node> {
    const response = await axiosInstance.post<ApiResponse<Node>>(`${this.baseUrl}/nodes/regenerate-token`, data);
    return response.data?.data || response.data;
  }

  // Cameras
  async getCamerasByNode(nodeId:  number): Promise<Camera[]> {
    const response = await axiosInstance.get<ApiResponse<Camera[]>>(`${this.baseUrl}/nodes/${nodeId}/cameras`);
    return response.data?.data || [];
  }

  async getAllCameras(): Promise<Camera[]> {
    const response = await axiosInstance.get<ApiResponse<Camera[]>>(`${this.baseUrl}/cameras`);
    return response.data?.data || [];
  }

  async getCameraById(cameraId: number): Promise<Camera> {
    const response = await axiosInstance.get<ApiResponse<Camera>>(`${this.baseUrl}/cameras/${cameraId}`);
    return response.data?.data || response.data;
  }

  async createCamera(data: CreateCameraDto): Promise<Camera> {
    const response = await axiosInstance.post<ApiResponse<Camera>>(`${this.baseUrl}/cameras`, data);
    return response.data?.data || response.data;
  }

  async updateCamera(cameraId: number, data: UpdateCameraDto): Promise<Camera> {
    const response = await axiosInstance.put<ApiResponse<Camera>>(`${this.baseUrl}/cameras/${cameraId}`, data);
    return response.data?.data || response.data;
  }

  async deleteCamera(cameraId: number): Promise<void> {
    await axiosInstance.delete(`${this.baseUrl}/cameras/${cameraId}`);
  }

  async getCameraLiveFeed(cameraId: number): Promise<string> {
    const response = await axiosInstance.get<ApiResponse<{ streamUrl: string }>>(`${this.baseUrl}/cameras/${cameraId}/live-feed`);
    return response.data?.data?.streamUrl || "";
  }
}

export const campusApi = new CampusApi();

